import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useDataRefresh, DEFAULT_POLL_INTERVAL_MS } from '@/hooks/useDataRefresh';
import { Plus, Search, FolderOpen, Edit, Calendar, DollarSign, LayoutGrid, Kanban, Hash } from 'lucide-react';
import { ProjectFormDialog } from '@/components/Projects/ProjectFormDialog';
import { ProjectKanbanView } from '@/components/Projects/ProjectKanbanView';
import type { Project } from '@/types/legacy';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function Projects() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'kanban'>('grid');

  const fetchProjects = async () => {
    try {
      const { data, error } = await api
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los proyectos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);
  useDataRefresh(fetchProjects, { pollIntervalMs: DEFAULT_POLL_INTERVAL_MS });

  const filteredProjects = projects.filter(project =>
    project.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.reference_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (project: any) => {
    if (!window.confirm(`¿Eliminar el proyecto "${project.name}"? Se marcará como inactivo y se conserva para trazabilidad.`)) return;
    const { error } = await api.from('projects').update({ is_active: false, updated_at: new Date().toISOString() }).eq('id', project.id);
    if (error) { toast({ title: 'Error', description: 'No se pudo eliminar: ' + error.message, variant: 'destructive' }); return; }
    toast({ title: 'Eliminado', description: 'Marcado como inactivo (histórico conservado).' });
    fetchProjects();
  };

  const handleEdit = (project: Project) => {
    setSelectedProject(project);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedProject(null);
    setDialogOpen(true);
  };

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      planning: { label: 'Planificación', variant: 'outline' },
      active: { label: 'Activo', variant: 'default' },
      paused: { label: 'Pausado', variant: 'secondary' },
      completed: { label: 'Completado', variant: 'default' },
      cancelled: { label: 'Cancelado', variant: 'destructive' },
    };
    const config = statusConfig[status] || statusConfig.planning;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
              Gestión de Proyectos
            </h1>
            <p className="text-muted-foreground mt-2">
              Proyectos y referencias de producción
            </p>
          </div>
          {canManage && (
            <Button onClick={handleNew} className="bg-gradient-magma hover:shadow-magma-hover">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Proyecto
            </Button>
          )}
        </div>

        {/* View Toggle + Search */}
        <div className="flex items-center gap-4">
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-none"
            >
              <LayoutGrid className="w-4 h-4 mr-2" />
              Grid
            </Button>
            <Button
              variant={viewMode === 'kanban' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('kanban')}
              className="rounded-none"
            >
              <Kanban className="w-4 h-4 mr-2" />
              Kanban
            </Button>
          </div>
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Buscar proyectos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Proyectos</p>
                <p className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                  {projects.length}
                </p>
              </div>
              <FolderOpen className="w-8 h-8 text-primary/50" />
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Activos</p>
                <p className="text-3xl font-bold text-success">
                  {projects.filter(p => p.status === 'active').length}
                </p>
              </div>
            </div>
          </Card>

          <Card className="glass-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completados</p>
                <p className="text-3xl font-bold text-primary">
                  {projects.filter(p => p.status === 'completed').length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Projects View */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : filteredProjects.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <FolderOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg text-muted-foreground">
              {searchTerm ? 'No se encontraron proyectos' : 'No hay proyectos registrados'}
            </p>
          </Card>
        ) : viewMode === 'kanban' ? (
          <ProjectKanbanView
            projects={filteredProjects}
            onSelectProject={(id) => {
              const project = projects.find(p => p.id === id);
              if (project) handleEdit(project);
            }}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="glass-card p-6 hover:shadow-magma transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <FolderOpen className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="gap-1 text-xs font-mono bg-primary/10">
                          P-{String((project as any).project_number || 0).padStart(4, '0')}
                        </Badge>
                        {project.reference_code && (
                          <Badge variant="secondary" className="text-xs">
                            {project.reference_code}
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-semibold text-lg">{project.name}</h3>
                    </div>
                  </div>
                  {!project.is_active && (
                    <Badge variant="secondary" className="text-xs">
                      Inactivo
                    </Badge>
                  )}
                </div>

                {project.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {project.description}
                  </p>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Estado:</span>
                    {getStatusBadge(project.status)}
                  </div>

                  {project.start_date && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {format(new Date(project.start_date), 'dd MMM yyyy', { locale: es })}
                        {project.end_date && ` - ${format(new Date(project.end_date), 'dd MMM yyyy', { locale: es })}`}
                      </span>
                    </div>
                  )}

                  {project.budget && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <DollarSign className="w-4 h-4" />
                      <span>{project.budget.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span>
                    </div>
                  )}

                  {(project.estimated_hours || project.actual_hours) && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Horas:</span>
                      <span className="font-medium">
                        {project.actual_hours}h
                        {project.estimated_hours && ` / ${project.estimated_hours}h`}
                      </span>
                    </div>
                  )}
                </div>

                {canManage && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(project)}
                      className="w-full"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Editar
                    </Button>
                    {project.is_active && <Button variant="outline" size="sm" className="w-full mt-2 text-destructive" onClick={() => handleDelete(project)}>Eliminar</Button>}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      <ProjectFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        project={selectedProject}
        onSuccess={fetchProjects}
      />
    </DashboardLayout>
  );
}
